/*
Service-worker update banner.

Listens for `controllerchange` on `navigator.serviceWorker`. When a new
service worker calls `clients.claim()` after `skipWaiting()` (which our
sw.js does on every activate), the controller field swaps to the new
worker and the event fires. At that point the page is still running
the OLD JS bundle, so we surface a banner the user can tap to reload
into the new version. Tap → `location.reload()`. Dismiss → hides the
banner; the next reload (any reason) picks up the new bundle.

First-install suppression: the very first time a service worker takes
over a previously-uncontrolled page, `controllerchange` also fires.
We don't want to flash "new version available" right after the user
adds the PWA to home screen, so we record whether a controller was
already active at install-time and only react to subsequent events.

The banner DOM lives outside `#app` (directly under `<body>`) so the
router's `inRoot.innerHTML = ''` between screens doesn't wipe it.
*/

const kBannerId: string = 'sw-update-banner'  // single global element id, used to dedupe re-injections

let fInstalled: boolean = false  // module-level guard, ensure we only wire the listener once

/*
Wire the controllerchange listener. Idempotent — a second call no-ops.
Safe to invoke before the SW is registered: if the API is missing
we bail silently.
*/
export function installSwUpdateBanner(): void
{
	if (fInstalled) return
	fInstalled = true
	if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return

	// `controller` is non-null when a SW is already actively managing
	// fetches for THIS page. On the very first install (no SW was
	// controlling at page load), it starts null and flips on the first
	// activate — that's the install transition, not an "update available"
	// signal, so we skip the first event.
	let vHadController: boolean = navigator.serviceWorker.controller !== null

	navigator.serviceWorker.addEventListener('controllerchange', () => {
		if (!vHadController)
		{
			vHadController = true
			return
		}
		showBanner()
	})
}

/*
Inject + reveal the banner. Idempotent: if a previous banner is still
in the DOM we just re-open it instead of stacking duplicates.
*/
function showBanner(): void
{
	const vExisting = document.getElementById(kBannerId)
	if (vExisting)
	{
		vExisting.setAttribute('data-open', 'true')
		return
	}

	const vBanner = document.createElement('div')
	vBanner.id = kBannerId
	vBanner.className = 'sw-update-banner'
	vBanner.setAttribute('role', 'status')
	vBanner.setAttribute('data-open', 'false')
	vBanner.innerHTML = `
		<button class="sw-update-banner-action" type="button">
			<span class="sw-update-banner-icon" aria-hidden="true">↻</span>
			<span class="sw-update-banner-text">Une nouvelle version est disponible — toucher pour recharger</span>
		</button>
		<button class="sw-update-banner-close" type="button" aria-label="Ignorer">×</button>
	`
	document.body.appendChild(vBanner)

	// Reveal in the next frame so the CSS transition has a non-zero
	// initial state to animate from.
	requestAnimationFrame(() => vBanner.setAttribute('data-open', 'true'))

	vBanner.querySelector<HTMLElement>('.sw-update-banner-action')?.addEventListener('click', () => {
		window.location.reload()
	})
	vBanner.querySelector<HTMLElement>('.sw-update-banner-close')?.addEventListener('click', () => {
		vBanner.setAttribute('data-open', 'false')
		window.setTimeout(() => vBanner.remove(), 220)
	})
}
